import { ApiError } from "./ApiError";
import { csrfHeaders } from "./csrf";
import { emitUnauthenticated, noteApiActivity } from "./sessionSignals";
import type { ApiErrorBody } from "./types";

const HTTP_NO_CONTENT = 204;
const HTTP_UNAUTHORIZED = 401;
const JSON_TYPE = "application/json";

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Minimal fetch wrapper for /api/*. Same-origin credentials, JSON in/out,
 * and CSRF handling: before any non-GET request it makes sure the
 * XSRF-TOKEN cookie exists (fetching GET /api/auth/csrf once) and echoes it
 * back as the X-XSRF-TOKEN header. On 401 it emits "unauthenticated"; every
 * 2xx response updates the last-success timestamp (see getLastApiSuccessAt).
 */
export async function apiClient<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: await requestHeaders(method, options.body !== undefined),
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });
  signalSession(response);
  return readResponse<T>(response);
}

async function requestHeaders(method: string, hasBody: boolean): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: JSON_TYPE };
  if (method !== "GET") Object.assign(headers, await csrfHeaders());
  if (hasBody) headers["Content-Type"] = JSON_TYPE;
  return headers;
}

/**
 * Only a real 401 means "not signed in". 429 (edge rate limit) and 5xx are transient and must
 * never log the user out.
 */
function signalSession(response: Response): void {
  if (response.status === HTTP_UNAUTHORIZED) emitUnauthenticated();
  if (response.ok) noteApiActivity(Date.now());
}

async function readResponse<T>(response: Response): Promise<T> {
  if (response.status === HTTP_NO_CONTENT) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes(JSON_TYPE) ? await response.json() : undefined;
  if (!response.ok) throw new ApiError(response.status, (payload as ApiErrorBody) ?? { error: "unknown_error" });
  return payload as T;
}
